// Data table: sortable columns, filter chips that carry counts, and explicit loading / empty / error states (never blank).
// Counts on chips count the rows in front of you; they are not a status and never replace an engine figure.
import { useMemo, useState, type ReactNode } from "react";
import { ErrorState, LoadingRows } from "./primitives";

export interface Column<T> {
  id: string; header: ReactNode; cell: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number | null; align?: "left" | "right"; className?: string;
}
export interface ChipFilter<T> { id: string; label: string; value: (row: T) => string; labelFor?: (value: string) => string }

export function DataTable<T>({ rows, columns, rowKey, filter, loading, error, onRetry, empty, onRowClick, caption, initialSort, dense }: {
  rows: T[] | undefined; columns: Array<Column<T>>; rowKey: (row: T) => string; filter?: ChipFilter<T>;
  loading?: boolean; error?: string; onRetry?: () => void; empty: ReactNode; onRowClick?: (row: T) => void; caption?: string;
  initialSort?: { id: string; dir: "asc" | "desc" }; dense?: boolean;
}) {
  const [sort, setSort] = useState(initialSort);
  const [chip, setChip] = useState<string | null>(null);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    if (filter && rows) for (const r of rows) { const v = filter.value(r); m.set(v, (m.get(v) ?? 0) + 1); }
    return m;
  }, [rows, filter]);
  const shown = useMemo(() => {
    let out = rows ?? [];
    if (filter && chip !== null) out = out.filter((r) => filter.value(r) === chip);
    const col = sort && columns.find((c) => c.id === sort.id);
    if (col?.sortValue) {
      const f = col.sortValue;
      out = [...out].sort((a, b) => {
        const x = f(a), y = f(b);
        const c = x === y ? 0 : x === null ? 1 : y === null ? -1 : x < y ? -1 : 1;
        return sort!.dir === "asc" ? c : -c;
      });
    }
    return out;
  }, [rows, chip, sort, columns, filter]);

  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (loading && !rows) return <LoadingRows />;
  return (
    <div className="space-y-2">
      {filter && rows && rows.length > 0 && (
        <div role="group" aria-label={`Filter by ${filter.label}`} className="flex flex-wrap gap-1.5">
          <Chip on={chip === null} onClick={() => setChip(null)} label="All" count={rows.length} />
          {[...counts.entries()].map(([v, n]) => <Chip key={v} on={chip === v} onClick={() => setChip(chip === v ? null : v)} label={filter.labelFor?.(v) ?? v} count={n} />)}
        </div>
      )}
      {!rows || rows.length === 0 ? <div className="rounded-md border border-dashed border-line px-4 py-8 text-center text-sm text-muted">{empty}</div> : (
        <div className="relative overflow-x-auto rounded-lg border border-line bg-raised">
          <table className="w-full text-[13px]">
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead className="border-b border-line bg-sunken text-left text-xs text-muted">
              <tr>
                {columns.map((c) => {
                  const on = sort?.id === c.id;
                  return (
                    <th key={c.id} scope="col" aria-sort={on ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined}
                      className={`px-3 py-2 font-medium ${c.align === "right" ? "text-right" : ""} ${c.className ?? ""}`}>
                      {c.sortValue ? (
                        <button type="button" className="inline-flex items-center gap-1 hover:text-ink"
                          onClick={() => setSort(on && sort!.dir === "asc" ? { id: c.id, dir: "desc" } : { id: c.id, dir: "asc" })}>
                          {c.header}<span aria-hidden="true">{on ? (sort!.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                        </button>
                      ) : c.header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {shown.map((r) => (
                <tr key={rowKey(r)} className={`${dense ? "h-8" : "h-10"} ${onRowClick ? "cursor-pointer hover:bg-sunken" : ""}`}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}>
                  {columns.map((c) => <td key={c.id} className={`px-3 py-1.5 align-middle ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.className ?? ""}`}>{c.cell(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && <p className="px-3 py-4 text-center text-sm text-muted">No rows match this filter.</p>}
        </div>
      )}
    </div>
  );
}

function Chip({ on, onClick, label, count }: { on: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} aria-label={`${label}, ${count}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${on ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line text-muted hover:text-ink"}`}>
      {label}<span className="tabular-nums">{count}</span>
    </button>
  );
}
